import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { useApiClient } from '@/api/ApiClientProvider'
import type { LeakageBasis, LifecycleAlign } from '@/api/revenueLeakage'
import type { GlobalFilters } from '@/lib/filters'
import { filtersKey } from '@/lib/useGlobalFilters'

/*
 * The lifecycle and leakage requests. Each key carries the client and the filters, and the
 * lifecycle's the basis too, so a gross curve never stands in for a net one (no leakage
 * figure depends on the basis); the previous answer stays on screen, marked busy, while
 * the next loads.
 */

export const leakageKeys = {
  all: ['revenue-stage-b'] as const,
  /** No basis: no leakage figure depends on it, so toggling it reads nothing again. */
  leakage: (kind: string, filters: GlobalFilters) => [...leakageKeys.all, 'leakage', kind, filtersKey(filters)] as const,
  lifecycle: (kind: string, filters: GlobalFilters, basis: LeakageBasis, ids: readonly string[], align: LifecycleAlign) =>
    [...leakageKeys.all, 'lifecycle', kind, filtersKey(filters), basis, ids.join(','), align] as const,
}

export function useRevenueLeakage(filters: GlobalFilters, basis: LeakageBasis) {
  const client = useApiClient()
  return useQuery({
    queryKey: leakageKeys.leakage(client.kind, filters),
    queryFn: () => client.revenueLeakage({ filters, basis }),
    placeholderData: keepPreviousData,
  })
}

export function useRevenueLifecycle(filters: GlobalFilters, basis: LeakageBasis, competitionIds: readonly string[], align: LifecycleAlign) {
  const client = useApiClient()
  return useQuery({
    queryKey: leakageKeys.lifecycle(client.kind, filters, basis, competitionIds, align),
    queryFn: () => client.revenueLifecycle({ filters, basis, competitionIds, align }),
    placeholderData: keepPreviousData,
    enabled: competitionIds.length > 0,
  })
}
